import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const CONTACT_TYPES = ["lead", "client", "investor", "lender", "subcontractor", "vendor", "architect", "engineer", "consultant", "inspector", "partner", "other"];

export default function Contacts() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState("");
    const [type, setType] = useState("");
    const [open, setOpen] = useState(false);

    const load = async () => {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        if (type) p.set("contact_type", type);
        const { data } = await api.get(`/contacts?${p}`);
        setRows(data.items || []);
        setTotal(data.total || 0);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, type]);

    return (
        <div className="p-6 md:p-10" data-testid="page-contacts">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="overline">Contacts</div>
                    <h1 className="mt-2 font-display text-4xl tracking-tight leading-none">People & Companies</h1>
                    <div className="text-sm text-muted-foreground mt-2">{total} total</div>
                </div>
                <button onClick={() => setOpen(true)} className="bg-foreground text-background px-4 py-2 text-sm inline-flex items-center gap-2 hover:opacity-90" data-testid="new-contact-btn">
                    <Plus size={14} /> New Contact
                </button>
            </div>

            <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-md">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company…" className="pl-9" data-testid="contact-search" />
                </div>
                <select value={type} onChange={(e) => setType(e.target.value)} className="border border-border bg-background px-3 py-2 text-sm" data-testid="contact-type-filter">
                    <option value="">All types</option>
                    {CONTACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>

            <div className="border border-border" data-testid="contacts-table">
                <div className="grid grid-cols-6 gap-3 px-4 py-3 border-b border-border text-xs uppercase tracking-widest text-muted-foreground">
                    <div className="col-span-2">Name</div><div>Type</div><div>Company</div><div>Email</div><div>Tags</div>
                </div>
                {rows.length === 0 && <div className="p-8 text-sm text-muted-foreground">No contacts yet.</div>}
                {rows.map((c) => (
                    <Link key={c.id} to={`/app/contacts/${c.id}`} className="grid grid-cols-6 gap-3 px-4 py-4 border-b border-border last:border-b-0 text-sm hover:bg-secondary/40" data-testid={`contact-row-${c.id}`}>
                        <div className="col-span-2 font-medium">{c.first_name} {c.last_name}</div>
                        <div className="text-xs text-muted-foreground capitalize">{c.contact_type}</div>
                        <div className="text-xs text-muted-foreground">{c.company || "—"}</div>
                        <div className="text-xs text-muted-foreground truncate">{c.email || "—"}</div>
                        <div className="text-xs text-muted-foreground flex gap-1 flex-wrap">{(c.tags || []).map((t) => <span key={t} className="px-1.5 py-0.5 border border-border">{t}</span>)}</div>
                    </Link>
                ))}
            </div>

            <NewContactDialog open={open} onOpenChange={setOpen} onCreated={() => { setOpen(false); load(); }} />
        </div>
    );
}

function NewContactDialog({ open, onOpenChange, onCreated }) {
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "", contact_type: "lead" });
    useEffect(() => { if (open) setForm({ first_name: "", last_name: "", email: "", phone: "", company: "", contact_type: "lead" }); }, [open]);
    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/contacts", form);
            toast.success("Contact created");
            onCreated();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="new-contact-dialog">
                <DialogHeader><DialogTitle className="font-display text-2xl">New contact</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required className="mt-2" data-testid="contact-first" /></div>
                        <div><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-2" data-testid="contact-last" /></div>
                    </div>
                    <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" data-testid="contact-email" /></div>
                    <div className="grid grid-cols-2 gap-4">
                        <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2" data-testid="contact-phone" /></div>
                        <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-2" data-testid="contact-company" /></div>
                    </div>
                    <div>
                        <Label>Type</Label>
                        <select value={form.contact_type} onChange={(e) => setForm({ ...form, contact_type: e.target.value })} className="mt-2 w-full border border-border bg-background px-3 py-2 text-sm" data-testid="contact-type">
                            {CONTACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <DialogFooter>
                        <button type="button" onClick={() => onOpenChange(false)} className="border border-border px-4 py-2 text-sm">Cancel</button>
                        <button type="submit" className="bg-foreground text-background px-4 py-2 text-sm hover:opacity-90" data-testid="contact-create-submit">Create</button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
